import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SaveSession } from "@/components/SaveSession";
import { FurtherReading } from "@/components/FurtherReading";
import { SCHEDULE_OPTIONS, ZONES } from "@/data/scheduledDrugs";
import {
  CheckCircle2, Circle, XCircle, Award, RotateCcw, ArrowRight, AlertTriangle,
  ShieldCheck, Lock, Boxes, ClipboardList, Tags, PackageCheck, Search, Timer, Flag, Zap,
} from "lucide-react";

/* schedule → chip styling */
const SCHED_CHIP = {
  unscheduled: "bg-teal-50 text-teal-700 border-teal-200",
  S3: "bg-indigo-50 text-indigo-700 border-indigo-200",
  S4: "bg-[#0047AB]/10 text-[#0047AB] border-[#0047AB]/30",
  S8: "bg-amber-50 text-amber-700 border-amber-300",
};
const SCHED_LABEL = { unscheduled: "Unscheduled", S3: "S3", S4: "S4", S8: "S8" };

/* ── phase stepper ─────────────────────────────────────────────────── */
const BASE_PHASES = [
  { key: "identify", label: "Identify schedule" },
  { key: "store", label: "Store correctly" },
  { key: "register", label: "S8 register" },
];
const INSPECT_PHASE = { key: "inspect", label: "Spot the breach" };

export const PhaseStepper = ({ phase, challenge = false }) => {
  const phases = challenge ? [...BASE_PHASES, INSPECT_PHASE] : BASE_PHASES;
  const order = [...phases.map((p) => p.key), "done"];
  const cur = order.indexOf(phase);
  return (
    <div className="flex items-center gap-1.5" data-testid="phase-stepper">
      {phases.map((p, i) => {
        const done = cur > i;
        const active = order[cur] === p.key;
        return (
          <div key={p.key} className="flex items-center gap-1.5 flex-1">
            <div
              data-testid={`phase-pill-${p.key}`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[11px] font-semibold w-full justify-center transition-colors ${
                active ? "bg-[#0047AB] text-white border-[#0047AB]"
                : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-white text-slate-400 border-slate-200"
              }`}
            >
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 text-center">{i + 1}</span>}
              <span className="truncate">{p.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── challenge-mode toggle ─────────────────────────────────────────── */
export const ChallengeCard = ({ enabled, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(!enabled)}
    data-testid="challenge-toggle"
    className={`w-full flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${
      enabled ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-300"
    }`}
  >
    <span className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${enabled ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-400"}`}>
      <Zap className="w-5 h-5" />
    </span>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-bold text-slate-900">Challenge mode {enabled && <span className="text-amber-600">· ON</span>}</div>
      <div className="text-[11px] text-slate-500 leading-snug">Adds a timed "spot the breach" dispensary inspection at the end — find every compliance breach before the clock runs out.</div>
    </div>
    <span className={`w-11 h-6 rounded-full relative transition-colors shrink-0 ${enabled ? "bg-amber-500" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
    </span>
  </button>
);


/* ── drug container card (generic photo + overlaid label) ───────────── */
export const DrugThumb = ({ drug, showSchedule = false, compact = false }) => (
  <div className="flex flex-col items-center text-center">
    <div className={`relative w-full ${compact ? "h-20" : "h-28"} rounded-md bg-gradient-to-b from-slate-50 to-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center`}>
      <img src={drug.img} alt={drug.name} className="h-full w-full object-contain p-1.5" loading="lazy" />
      {showSchedule && (
        <span className={`absolute top-1 right-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${SCHED_CHIP[drug.schedule]}`}>
          {SCHED_LABEL[drug.schedule]}
        </span>
      )}
    </div>
    <div className={`mt-1.5 font-semibold leading-tight text-slate-900 ${compact ? "text-[11px]" : "text-[12px]"}`}>{drug.name}</div>
    {!compact && <div className="text-[10px] text-slate-500">{drug.active} · {drug.strength}</div>}
  </div>
);

/* ── PHASE 1 · identify the schedule ────────────────────────────────── */
export const IdentifyPanel = ({ drugs, answers, onAnswer, doneCount, onProceed }) => (
  <Card className="border-slate-200" data-testid="identify-panel">
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
        <Tags className="w-4 h-4 text-[#0047AB]" /> Identify each drug's schedule
        <span className="ml-auto text-xs font-mono text-[#0047AB]" data-testid="identify-progress">{doneCount}/{drugs.length}</span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-[13px] text-slate-600 mb-4">
        Read each medicine, then classify it under the Poisons Standard. You get one attempt per drug — choose carefully.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {drugs.map((d) => {
          const a = answers[d.id];
          const answered = !!a;
          const correct = answered && a === d.schedule;
          return (
            <div key={d.id} className={`rounded-lg border-2 p-3 transition-colors ${answered ? (correct ? "border-emerald-300 bg-emerald-50/40" : "border-rose-300 bg-rose-50/40") : "border-slate-200 bg-white"}`} data-testid={`identify-card-${d.id}`}>
              <DrugThumb drug={d} compact />
              {!answered ? (
                <div className="grid grid-cols-2 gap-1.5 mt-2.5">
                  {SCHEDULE_OPTIONS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => onAnswer(d, o.id)}
                      data-testid={`identify-${d.id}-opt-${o.id}`}
                      className="text-[11px] font-semibold border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 hover:border-[#0047AB] hover:bg-[#0047AB]/5 transition-colors"
                    >
                      {o.label}
                      <span className="block text-[9px] font-normal text-slate-400 leading-tight">{o.hint}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-2.5">
                  <div className={`flex items-center gap-1.5 text-[11px] font-bold ${correct ? "text-emerald-700" : "text-rose-700"}`} data-testid={`identify-${d.id}-result`}>
                    {correct ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {correct ? "Correct" : `Incorrect — it's ${SCHED_LABEL[d.schedule]}`}
                  </div>
                  <p className="text-[10.5px] text-slate-500 leading-snug mt-1">{d.note}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Button onClick={onProceed} disabled={doneCount < drugs.length} data-testid="identify-proceed-btn"
        className="w-full mt-4 bg-[#0047AB] hover:bg-[#003380] text-white disabled:opacity-40">
        Proceed to storage <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
      {doneCount < drugs.length && <p className="text-[11px] text-slate-400 text-center mt-1">Classify every drug to continue.</p>}
    </CardContent>
  </Card>
);

/* ── PHASE 2 · store each drug in the right place ───────────────────── */
const ZONE_ICON = { general: Boxes, cabinet: Lock, safe: ShieldCheck };

export const StorePanel = ({ drugs, placements, selectedId, onSelect, onPlace, placedCount, onProceed }) => {
  const unplaced = drugs.filter((d) => !placements[d.id]);
  return (
    <Card className="border-slate-200" data-testid="store-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <PackageCheck className="w-4 h-4 text-[#0047AB]" /> Put each drug away correctly
          <span className="ml-auto text-xs font-mono text-[#0047AB]" data-testid="store-progress">{placedCount}/{drugs.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-[13px] text-slate-600">
          Select a medicine, then tap the storage location it belongs in.
          {selectedId ? <span className="ml-1 font-semibold text-[#0047AB]">Now choose a location ↓</span> : <span className="ml-1 text-slate-400">Tap a drug to begin.</span>}
        </p>

        {/* tray of unplaced drugs */}
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="drug-tray">
          {unplaced.length === 0 ? (
            <div className="text-center text-emerald-700 text-sm font-semibold py-3 flex items-center justify-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> All medicines stored.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {unplaced.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onSelect(d.id)}
                  data-testid={`store-drug-${d.id}`}
                  className={`rounded-md border-2 p-1.5 text-left transition-all ${selectedId === d.id ? "border-[#0047AB] bg-[#0047AB]/5 ring-2 ring-[#0047AB]/20 scale-[1.03]" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <DrugThumb drug={d} compact />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* zones */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {ZONES.map((z) => {
            const Icon = ZONE_ICON[z.id];
            const inZone = drugs.filter((d) => placements[d.id] === z.id);
            return (
              <button
                key={z.id}
                type="button"
                disabled={!selectedId}
                onClick={() => onPlace(z.id)}
                data-testid={`store-zone-${z.id}`}
                className={`text-left rounded-lg border-2 p-3 min-h-[140px] transition-colors ${selectedId ? "border-dashed hover:bg-slate-50 cursor-pointer" : "border-solid cursor-default"}`}
                style={{ borderColor: selectedId ? z.accent : "#e2e8f0" }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-7 h-7 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: z.accent }}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[12px] font-bold text-slate-900 leading-tight">{z.label}</div>
                    <div className="text-[10px] text-slate-500">{z.sub}</div>
                  </div>
                </div>
                {inZone.length === 0 ? (
                  <p className="text-[10px] text-slate-400 leading-snug mt-1">{z.desc}</p>
                ) : (
                  <div className="grid grid-cols-3 gap-1 mt-1.5">
                    {inZone.map((d) => (
                      <div key={d.id} className="rounded bg-white border border-slate-200 p-0.5" data-testid={`placed-${d.id}`} title={d.name}>
                        <img src={d.img} alt={d.name} className="h-9 w-full object-contain" />
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <Button onClick={onProceed} disabled={placedCount < drugs.length} data-testid="store-proceed-btn"
          className="w-full bg-[#0047AB] hover:bg-[#003380] text-white disabled:opacity-40">
          Proceed to the S8 register <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
};

/* ── PHASE 3 · S8 drug register reconciliation ──────────────────────── */
export const RegisterPanel = ({ scenario, vm }) => (
  <Card className="border-slate-200" data-testid="register-panel">
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-[#0047AB]" /> S8 controlled-drug register
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="text-[13px] text-slate-700">
        Drug: <span className="font-semibold text-slate-900">{scenario.drug}</span>
      </div>

      {/* running register */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">Transaction</th>
              <th className="text-right px-3 py-2">Out ({scenario.unit})</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr className="bg-emerald-50/40">
              <td className="px-3 py-2 text-slate-400">—</td>
              <td className="px-3 py-2 font-semibold text-slate-800">Opening balance</td>
              <td className="px-3 py-2 text-right font-mono-clinical text-slate-800">{scenario.opening}</td>
            </tr>
            {scenario.events.map((e) => (
              <tr key={e.id} data-testid={`register-event-${e.id}`}>
                <td className="px-3 py-2 text-slate-500 font-mono-clinical">{e.time}</td>
                <td className="px-3 py-2 text-slate-700">{e.desc}</td>
                <td className="px-3 py-2 text-right font-mono-clinical text-rose-600">−{e.out}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* closing balance */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <label className="text-[12px] font-semibold text-slate-700">Enter the correct closing balance ({scenario.unit})</label>
        <div className="flex items-center gap-2 mt-1.5">
          <Input
            type="number"
            value={vm.closingInput}
            disabled={vm.closingConfirmed}
            onChange={(e) => vm.setClosingInput(e.target.value)}
            data-testid="register-closing-input"
            className="w-32 font-mono-clinical"
            placeholder="mg"
          />
          {!vm.closingConfirmed ? (
            <Button size="sm" onClick={vm.onConfirmClosing} data-testid="register-closing-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white">
              Confirm balance
            </Button>
          ) : (
            <span className={`flex items-center gap-1 text-[12px] font-bold ${vm.closingCorrect ? "text-emerald-700" : "text-rose-700"}`} data-testid="register-closing-result">
              {vm.closingCorrect ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {vm.closingCorrect ? "Correct" : `Should be ${scenario.expectedClosing} ${scenario.unit}`}
            </span>
          )}
        </div>
      </div>

      {/* discrepancy action — revealed after closing confirmed */}
      {vm.closingConfirmed && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3" data-testid="register-discrepancy">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-800 mb-1.5">
            <AlertTriangle className="w-4 h-4" /> Physical count of the vial = {scenario.physicalCount} {scenario.unit}. What do you do?
          </div>
          <div className="space-y-1.5">
            {scenario.actions.map((act) => {
              const chosen = vm.actionChoice === act.id;
              const reveal = vm.actionChoice != null;
              let cls = "border-slate-200 bg-white text-slate-700 hover:border-[#0047AB]";
              if (reveal && chosen) cls = act.correct ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-rose-400 bg-rose-50 text-rose-700";
              else if (reveal && act.correct) cls = "border-emerald-300 bg-emerald-50/50 text-emerald-700";
              return (
                <button key={act.id} type="button" disabled={vm.actionChoice != null} onClick={() => vm.onAction(act.id)}
                  data-testid={`register-action-${act.id}`}
                  className={`w-full text-left text-[12px] font-medium border-2 rounded-md px-3 py-2 transition-colors disabled:cursor-default ${cls}`}>
                  {act.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* compliance safeguards multi-select */}
      {vm.actionChoice != null && (
        <div className="rounded-md border border-slate-200 p-3" data-testid="register-safeguards">
          <div className="text-[12px] font-semibold text-slate-700 mb-2">Tick the correct S8 record-keeping safeguards (some are wrong):</div>
          <div className="space-y-1.5">
            {scenario.safeguards.map((s) => {
              const sel = vm.safeguards.includes(s.id);
              return (
                <button key={s.id} type="button" disabled={vm.safeguardsLocked} onClick={() => vm.toggleSafeguard(s.id)}
                  data-testid={`register-safeguard-${s.id}`}
                  className={`w-full flex items-start gap-2 text-left text-[12px] border rounded-md px-3 py-2 transition-colors disabled:cursor-default ${
                    vm.safeguardsLocked
                      ? (s.correct ? "border-emerald-300 bg-emerald-50/50" : sel ? "border-rose-300 bg-rose-50/50" : "border-slate-200 bg-white opacity-70")
                      : sel ? "border-[#0047AB] bg-[#0047AB]/5" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}>
                  {sel ? <CheckCircle2 className="w-4 h-4 text-[#0047AB] shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
                  <span className="text-slate-700">{s.label}</span>
                  {vm.safeguardsLocked && (s.correct ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto shrink-0" /> : sel ? <XCircle className="w-3.5 h-3.5 text-rose-500 ml-auto shrink-0" /> : null)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Button onClick={vm.onFinish} disabled={!vm.canFinish} data-testid="register-finish-btn"
        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40">
        Finish &amp; see results <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </CardContent>
  </Card>
);

/* ── PHASE 4 (challenge) · spot-the-breach inspection ───────────────── */
/* Neutral palette only — the learner must read the scene (door open? vial out?
   blank register row? expiry date?) rather than just clicking coloured items. */
const SLATE = "#475569", LINE = "#94a3b8", FILL = "#e2e8f0", DARK = "#1e293b", BLUE = "#0047AB";

const StationArt = ({ type, breach }) => {
  const body = (children) => <svg viewBox="0 0 96 64" className="w-full h-[60px]">{children}</svg>;
  switch (type) {
    case "cabinet":
      return body(breach ? (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <rect x="30" y="8" width="36" height="48" rx="2" fill="#f1f5f9" />
          <path d="M30 10 L10 4 L10 52 L30 56" fill="#e2e8f0" />
          <path d="M66 10 L86 4 L86 52 L66 56" fill="#e2e8f0" />
          <rect x="40" y="20" width="6" height="16" fill={LINE} stroke="none" />
          <rect x="50" y="20" width="6" height="16" fill={LINE} stroke="none" />
        </g>
      ) : (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <rect x="22" y="8" width="52" height="48" rx="2" fill="#f1f5f9" />
          <line x1="48" y1="8" x2="48" y2="56" />
          <circle cx="58" cy="32" r="3.5" fill={BLUE} stroke="none" />
          <circle cx="38" cy="32" r="3.5" fill={LINE} stroke="none" />
        </g>
      ));
    case "stock":
      return body(
        <g>
          <rect x="26" y="14" width="44" height="38" rx="2" fill="#f1f5f9" stroke={SLATE} strokeWidth="2" />
          <path d="M26 22 H70" stroke={LINE} strokeWidth="1.5" />
          <rect x="34" y="28" width="28" height="14" rx="2" fill="#fff" stroke={LINE} strokeWidth="1" />
          <text x="48" y="38" fontSize="8" fontFamily="monospace" textAnchor="middle" fill={DARK}>{breach ? "EXP 02/24" : "EXP 08/27"}</text>
        </g>
      );
    case "keys":
      return body(breach ? (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <rect x="26" y="10" width="32" height="44" rx="2" fill="#f1f5f9" />
          <path d="M58 10 L74 6 L74 50 L58 54" fill="#e2e8f0" />
          <circle cx="42" cy="26" r="5" />
          <line x1="42" y1="31" x2="42" y2="44" />
          <line x1="42" y1="40" x2="47" y2="40" />
        </g>
      ) : (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <rect x="30" y="10" width="36" height="44" rx="2" fill="#f1f5f9" />
          <circle cx="48" cy="30" r="3" fill={LINE} stroke="none" />
          <line x1="48" y1="33" x2="48" y2="42" />
        </g>
      ));
    case "safe":
      return body(breach ? (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <rect x="14" y="8" width="48" height="48" rx="3" fill="#cbd5e1" />
          <rect x="20" y="14" width="36" height="36" fill="#94a3b8" />
          <rect x="30" y="22" width="6" height="14" fill="#e2e8f0" stroke={SLATE} strokeWidth="1" />
          <rect x="40" y="22" width="6" height="14" fill="#e2e8f0" stroke={SLATE} strokeWidth="1" />
          <path d="M62 12 L84 6 L84 52 L62 54" fill="#e2e8f0" />
          <path d="M74 24 a6 6 0 0 1 12 0" />
        </g>
      ) : (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <rect x="24" y="8" width="48" height="48" rx="3" fill="#cbd5e1" />
          <rect x="30" y="14" width="36" height="36" fill="#94a3b8" />
          <circle cx="48" cy="32" r="8" fill="#e2e8f0" />
          <circle cx="48" cy="32" r="2.5" fill={DARK} stroke="none" />
          <path d="M44 24 a4 4 0 0 1 8 0 v3 h-8 z" fill={BLUE} stroke="none" />
        </g>
      ));
    case "bench":
      return body(breach ? (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <line x1="14" y1="46" x2="82" y2="46" />
          <rect x="30" y="30" width="10" height="16" rx="2" fill="#cfe3ee" />
          <rect x="32" y="26" width="6" height="5" fill={LINE} stroke="none" />
          <line x1="46" y1="40" x2="70" y2="34" />
          <rect x="44" y="38" width="6" height="5" fill={LINE} stroke="none" transform="rotate(-14 47 40)" />
          <line x1="66" y1="35" x2="72" y2="33" strokeWidth="3" />
        </g>
      ) : (
        <g fill="none" stroke={SLATE} strokeWidth="2">
          <line x1="14" y1="46" x2="82" y2="46" />
          <rect x="34" y="38" width="28" height="8" rx="2" fill="#e2e8f0" />
        </g>
      ));
    case "register":
      return body(
        <g>
          <rect x="28" y="8" width="40" height="48" rx="2" fill="#fff" stroke={SLATE} strokeWidth="2" />
          <rect x="33" y="13" width="30" height="5" rx="1" fill={BLUE} opacity="0.7" />
          {[24, 32, 40, 48].map((y, i) => {
            const blankRow = breach && i === 2;
            return (
              <g key={y}>
                <rect x="33" y={y} width={blankRow ? 30 : 20} height="4" rx="1"
                  fill={blankRow ? "none" : "#cbd5e1"} stroke={blankRow ? "#64748b" : "none"} strokeDasharray={blankRow ? "2 2" : "0"} strokeWidth="1" />
                {!blankRow && <rect x="56" y={y - 0.5} width="7" height="5" rx="1" fill={LINE} />}
              </g>
            );
          })}
        </g>
      );
    default:
      return null;
  }
};

export const InspectPanel = ({ stations, found, revealed, timeLeft, totalBreaches, onToggle, onFinish }) => {
  const mm = String(Math.floor(timeLeft / 60)).padStart(1, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");
  const foundBreaches = stations.filter((s) => s.breach && found.includes(s.id)).length;
  const low = timeLeft <= 10 && !revealed;
  return (
    <Card className="border-amber-200" data-testid="inspect-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <Search className="w-4 h-4 text-amber-600" /> Spot the breach — dispensary inspection
          <span className={`ml-auto flex items-center gap-1 font-mono text-sm px-2 py-0.5 rounded ${low ? "bg-rose-100 text-rose-700 animate-pulse" : "bg-slate-100 text-slate-700"}`} data-testid="inspect-timer">
            <Timer className="w-3.5 h-3.5" /> {mm}:{ss}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-[13px] text-slate-600 mb-3">
          Walk the dispensary and <span className="font-semibold text-slate-900">tap every compliance breach</span> you can find before the timer runs out. Tapping a compliant item counts against you.
          <span className="ml-1 font-mono text-amber-700" data-testid="inspect-found">{foundBreaches}/{totalBreaches} found</span>
        </p>

        {/* the scene */}
        <div
          className="relative w-full rounded-xl border border-slate-300 overflow-hidden"
          style={{ aspectRatio: "16 / 8", background: "linear-gradient(to bottom, #eef2f7 0%, #eef2f7 64%, #d9dee6 64%, #cdd4de 100%)" }}
          data-testid="inspect-scene"
        >
          <div className="absolute left-0 right-0" style={{ top: "63%", borderTop: "1px solid #b6bfca" }} />
          {stations.map((st) => {
            const flagged = found.includes(st.id);
            let badge = null, ring = "border-slate-200";
            if (revealed) {
              if (st.breach && flagged) { badge = { c: "text-emerald-600", Icon: CheckCircle2, t: "Breach" }; ring = "border-emerald-400"; }
              else if (st.breach && !flagged) { badge = { c: "text-rose-600", Icon: AlertTriangle, t: "Missed" }; ring = "border-rose-400"; }
              else if (!st.breach && flagged) { badge = { c: "text-amber-600", Icon: XCircle, t: "False alarm" }; ring = "border-amber-400"; }
              else { ring = "border-slate-200 opacity-70"; }
            } else if (flagged) {
              ring = "border-amber-500 ring-2 ring-amber-300";
            }
            return (
              <button
                key={st.id}
                type="button"
                disabled={revealed}
                onClick={() => onToggle(st.id)}
                data-testid={`inspect-station-${st.id}`}
                style={{ left: `${st.x}%`, top: `${st.y}%`, width: "26%" }}
                className={`absolute rounded-lg border-2 bg-white/90 backdrop-blur p-1.5 text-center transition-all ${ring} ${!revealed && "hover:border-amber-400 hover:-translate-y-0.5"}`}
              >
                <StationArt type={st.type} breach={st.breach} />
                <div className="text-[9.5px] font-semibold text-slate-600 leading-tight truncate">{st.label}</div>
                {flagged && !revealed && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center shadow">
                    <Flag className="w-3 h-3" />
                  </span>
                )}
                {revealed && badge && (
                  <span className={`absolute -top-2 -right-2 bg-white rounded-full shadow border ${badge.c}`} title={badge.t}>
                    <badge.Icon className="w-4 h-4" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {!revealed ? (
          <Button onClick={onFinish} data-testid="inspect-finish-btn" className="w-full mt-4 bg-amber-600 hover:bg-amber-500 text-white">
            Finish inspection ({foundBreaches}/{totalBreaches} flagged) <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        ) : (
          <ul className="mt-4 space-y-1.5 text-[12px] border-t border-slate-100 pt-3" data-testid="inspect-results">
            {stations.filter((s) => s.breach).map((s) => {
              const hit = found.includes(s.id);
              return (
                <li key={s.id} className="flex items-start gap-1.5">
                  {hit ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" />}
                  <span className={hit ? "text-slate-600" : "text-rose-700"}><span className="font-semibold">{s.label}:</span> {s.breachDetail}</span>
                </li>
              );
            })}
            {stations.filter((s) => !s.breach && found.includes(s.id)).map((s) => (
              <li key={s.id} className="flex items-start gap-1.5">
                <XCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                <span className="text-amber-700"><span className="font-semibold">{s.label}:</span> false alarm — this was compliant. {s.okDetail}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

/* ── debrief ────────────────────────────────────────────────────────── */
export const StorageDebrief = ({ result, onReset }) => {
  const band = result.total >= 80 ? { t: "text-emerald-600", r: "border-emerald-300 bg-emerald-50", label: "Competent" }
    : result.total >= 60 ? { t: "text-amber-600", r: "border-amber-300 bg-amber-50", label: "Developing" }
    : { t: "text-rose-600", r: "border-rose-300 bg-rose-50", label: "Not yet competent" };
  return (
    <div className="space-y-5 fade-up">
      <Card className="border-emerald-200 shadow-lg" data-testid="storage-debrief">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${band.r}`} data-testid="storage-grade">
                <Award className={`w-5 h-5 ${band.t}`} />
                <span className={`text-4xl font-bold leading-none ${band.t}`} data-testid="storage-score">{result.total}</span>
                <span className="text-[10px] text-slate-400 font-semibold">/100</span>
              </div>
              <div>
                <div className={`text-xl font-bold ${band.t}`} data-testid="storage-band">{band.label}</div>
                <div className="text-xs text-slate-500 mt-1">Grade {result.grade} · Cert IV scheduled-drug handling</div>
              </div>
            </div>
            <div className="lg:col-span-2" data-testid="storage-breakdown">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Phase scores</div>
              <div className="space-y-2">
                {result.phases.map((p) => {
                  const pct = Math.round((p.score / p.max) * 100);
                  return (
                    <div key={p.key} data-testid={`storage-phase-${p.key}`}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="text-slate-700 font-medium">{p.label}</span>
                        <span className="font-mono-clinical font-semibold text-slate-800">{p.score}/{p.max}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {result.mistakes.length > 0 && (
            <ul className="mt-4 space-y-1 text-[11px] text-slate-500 border-t border-slate-100 pt-3" data-testid="storage-mistakes">
              {result.mistakes.map((m, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <XCircle className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                  <span><span className="uppercase text-[9px] font-bold text-slate-400 mr-1">{m.phase}</span>{m.msg}</span>
                </li>
              ))}
            </ul>
          )}
          <Button onClick={onReset} data-testid="storage-reset-btn" className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Run it again
          </Button>
        </CardContent>
      </Card>
      <SaveSession
        simulatorId="drugstorage"
        simulatorName="Scheduled Drug Storage & Compliance"
        score={result.total}
        grade={band.label}
        summary={`Grade ${result.grade} · ${result.phases.map((p) => `${p.label.toLowerCase()} ${p.score}/${p.max}`).join(" · ")}`}
      />
      <FurtherReading topic="scheduling" />
    </div>
  );
};
